import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  StudyMedicalHistoryError,
  StudyMedicalHistoryRequest,
  StudyMedicalHistoryResponse,
  StudyTechnologistNotesError,
  StudyTechnologistNotesRequest,
  StudyTechnologistNotesResponse,
} from '@/api/model';

const KEY = {
  STUDY_MEDICAL_HISTORY: 'STUDY_MEDICAL_HISTORY',
  STUDY_TECHNOLOGIST_NOTES: 'STUDY_TECHNOLOGIST_NOTES',
} as const;

const request = {
  getMedicalHistory: async (
    { payload: { studyId } }: StudyMedicalHistoryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.HI.get<StudyMedicalHistoryResponse>(
      `${ENDPOINTS.HI.STUDY}${studyId}/medical-history`,
      { signal },
    );

    return data;
  },
  getTechnologistNotes: async (
    { payload: { studyId } }: StudyTechnologistNotesRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.HI.get<StudyTechnologistNotesResponse>(
      `${ENDPOINTS.HI.STUDY}${studyId}/technologistNotes`,
      { signal },
    );

    return data;
  },
};

const query = {
  useGetMedicalHistory: (
    { payload }: StudyMedicalHistoryRequest,
    config?: Partial<UseQueryOptions<StudyMedicalHistoryResponse, StudyMedicalHistoryError>>,
  ) =>
    useQuery<StudyMedicalHistoryResponse, StudyMedicalHistoryError>({
      queryFn: ({ signal }) => request.getMedicalHistory({ payload }, signal),
      queryKey: [KEY.STUDY_MEDICAL_HISTORY, payload.studyId],
      ...config,
    }),
  useGetTechnologistNotes: (
    { payload }: StudyTechnologistNotesRequest,
    config?: Partial<UseQueryOptions<StudyTechnologistNotesResponse, StudyTechnologistNotesError>>,
  ) =>
    useQuery<StudyTechnologistNotesResponse, StudyTechnologistNotesError>({
      queryFn: ({ signal }) => request.getTechnologistNotes({ payload }, signal),
      queryKey: [KEY.STUDY_TECHNOLOGIST_NOTES, payload.studyId],
      ...config,
    }),
};

export const study = {
  KEY,
  query,
  request,
};
